// import styles
import "./AutomezziDashboard.css";

// import pacchetti
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// import hooks
import { useCollection } from "../../hooks/useCollection";

// import componenti
import AutomezziFilter from "./AutomezziFilter";

export default function AutomezziDashboard() {
  const { documents: automezziDocs } = useCollection("automezzi");
  const { documents: settoriDocs } = useCollection("settori");
  const [activeFilter, setActiveFilter] = useState("tutti");
  const [settoriOptions, setSettoriOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "modello", dir: "asc" });
  const navigazione = useNavigate();

  // Preparazione delle opzioni per il filtro dei settori
  useEffect(() => {
    if (settoriDocs) {
      const options = settoriDocs.map((settore) => {
        return settore.nome;
      });
      setSettoriOptions(options);
    }
  }, [settoriDocs]);

  const changeFilter = (newFilter) => {
    setActiveFilter(newFilter);
  };

  // Gestisce il termine di ricerca dal componente AutomezziFilter
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const filteredAutomezzi = useMemo(() => {
    if (!automezziDocs) return null;

    return automezziDocs.filter((automezzo) => {
      // Filtraggio per settore
      const matchesFilter =
        activeFilter === "tutti" || automezzo.settoreNome === activeFilter;

      // Filtraggio per termine di ricerca
      const matchesSearch =
        searchTerm === "" ||
        (automezzo.modello || "").toLowerCase().includes(searchTerm) ||
        (automezzo.targa || "").toLowerCase().includes(searchTerm) ||
        (automezzo.carburante || "").toLowerCase().includes(searchTerm) ||
        (automezzo.settoreNome || "").toLowerCase().includes(searchTerm) ||
        (automezzo.autostrade || "").toLowerCase().includes(searchTerm);

      // Deve corrispondere sia al filtro che al termine di ricerca
      return matchesFilter && matchesSearch;
    });
  }, [automezziDocs, activeFilter, searchTerm]);

  const sortedAutomezzi = useMemo(() => {
    if (!filteredAutomezzi) return null;

    const collator = new Intl.Collator("it", {
      sensitivity: "base",
      numeric: true,
    });

    const getValue = (item, key) => {
      switch (key) {
        case "modello":
          return (item.modello || "").toLowerCase();
        case "targa":
          return (item.targa || "").toLowerCase();
        case "settore":
          return (item.settoreNome || "").toLowerCase();
        case "carburante":
          return (item.carburante || "").toLowerCase();
        case "autostrade":
          return (item.autostrade || "").toLowerCase();
        default:
          return (item.modello || "").toLowerCase();
      }
    };

    return [...filteredAutomezzi].sort((a, b) => {
      const aVal = getValue(a, sortConfig.key);
      const bVal = getValue(b, sortConfig.key);
      
      let cmp = 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        cmp = aVal - bVal;
      } else {
        cmp = collator.compare(String(aVal), String(bVal));
      }

      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
  }, [filteredAutomezzi, sortConfig]);

  const handleHeaderSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  return (
    <div>
      <div className="title-container">
        <h2 className="page-title">Dashboard Automezzi</h2>
        <button onClick={() => navigazione("/automezzi")} className="btn">
          Gestisci Automezzi
        </button>
      </div>

      {automezziDocs && settoriOptions.length > 0 && (
        <AutomezziFilter
          filtro={activeFilter}
          change={changeFilter}
          settori={settoriOptions}
          onSearch={handleSearch}
        />
      )}

      {sortedAutomezzi && (
        <div className="automezzi-table-wrapper">
          <table className="automezzi-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("modello")}
                >
                  Modello{" "}
                  {sortConfig.key === "modello"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("targa")}
                >
                  Targa{" "}
                  {sortConfig.key === "targa"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("settore")}
                >
                  Settore{" "}
                  {sortConfig.key === "settore"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("carburante")}
                >
                  Carburante{" "}
                  {sortConfig.key === "carburante"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleHeaderSort("autostrade")}
                >
                  Autostrade{" "}
                  {sortConfig.key === "autostrade"
                    ? sortConfig.dir === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedAutomezzi.map((automezzo) => (
                <tr
                  key={automezzo.id}
                  className="clickable-row"
                  onClick={() => navigazione(`/automezzi?id=${automezzo.id}`)}
                >
                  <td>{automezzo.modello}</td>
                  <td>{automezzo.targa}</td>
                  <td>{automezzo.settoreNome}</td>
                  <td>{automezzo.carburante}</td>
                  <td>{automezzo.autostrade}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedAutomezzi.length === 0 && (
            <p className="no-data">Nessun automezzo trovato con i filtri selezionati</p>
          )}
        </div>
      )}
    </div>
  );
}
